import { Text as RNText, View } from "react-native";
import {
	VictoryAxis,
	VictoryBar,
	VictoryChart,
	VictoryLabel,
	VictoryLine,
	VictoryTheme,
} from "victory-native";
import { useAppTheme } from "../../../useAppTheme";
import { CHART_DIMENSIONS } from "../constants";
import { useChartFontScale } from "../hooks/useChartFontScale";
import type { BarChartData } from "../types";

type TimeDistributionBarChartProps = {
	data: BarChartData;
	threshold?: number;
	containerWidth: number;
};

const getBarColor = (
	item: BarChartData[number],
	threshold: number | undefined,
	colors: { success: string; warning: string; error: string; primary: string },
): string => {
	if (threshold !== undefined) {
		if (item.minutes >= threshold) {
			return colors.success;
		}
		if (item.minutes >= threshold - 6) {
			return colors.warning;
		}
		return colors.error;
	}

	if (item.status) {
		switch (item.status) {
			case "quick":
				return colors.success;
			case "average":
				return colors.warning;
			case "slow":
				return colors.error;
			default:
				return colors.primary;
		}
	}

	return colors.primary;
};

const BAR_CHART_ASPECT_RATIO = 0.52;
const SIDE_PADDING = 8;

const formatXAxisLabel = (label: string) => {
	const words = label.split(/\s+/).filter(Boolean);
	if (words.length === 1 && label.length > 14) {
		return `${label.slice(0, 13)}…`;
	}
	if (words.length === 2 && label.length > 16) {
		const second = words[1] ?? "";
		return `${words[0]}\n${second.length > 12 ? `${second.slice(0, 11)}…` : second}`;
	}
	if (words.length <= 2 && label.length <= 18) {
		return label;
	}
	const firstLine = words.slice(0, 2).join(" ");
	const rest = words.slice(2).join(" ");
	if (!rest) {
		return firstLine;
	}
	return `${firstLine}\n${rest.length > 12 ? `${rest.slice(0, 11)}…` : rest}`;
};

export const TimeDistributionBarChart = ({
	data,
	threshold,
	containerWidth,
}: TimeDistributionBarChartProps) => {
	const theme = useAppTheme();
	const config = CHART_DIMENSIONS.barChart;
	const fontScale = useChartFontScale(containerWidth);

	const scale = containerWidth / config.referenceWidth;
	const scaledBarWidth = config.barWidth * scale;
	const scaledDomainPaddingX = config.domainPaddingX * scale;

	const chartWidth = Math.max(containerWidth - SIDE_PADDING * 2, 280);
	const chartHeight = Math.round(containerWidth * BAR_CHART_ASPECT_RATIO);

	const colors = {
		success: theme.colors.confidence?.high ?? theme.colors.progress.high,
		warning: theme.colors.confidence?.medium ?? theme.colors.progress.medium,
		error: theme.colors.confidence?.low ?? theme.colors.progress.low,
		primary: theme.colors.primary,
	};

	const chartData = data.map((item, index) => ({
		x: formatXAxisLabel(item.category),
		y: item.minutes,
		fill: getBarColor(item, threshold, colors),
		order: index,
	}));

	const maxYValue = Math.max(...data.map((d) => d.minutes), threshold || 0);
	const yDomain = Math.ceil(maxYValue * 1.2);

	const targetLabelY =
		threshold !== undefined
			? config.padding.top +
				(1 - threshold / yDomain) *
					(chartHeight - config.padding.top - config.padding.bottom)
			: 0;

	const targetFontSize = config.axisLabelFontSize * fontScale;

	return (
		<View style={{ position: "relative" }}>
			<VictoryChart
				theme={VictoryTheme.material}
				domainPadding={{ x: [scaledDomainPaddingX, scaledDomainPaddingX] }}
				domain={{ y: [0, yDomain] }}
				height={chartHeight}
				width={chartWidth}
				padding={config.padding}
			>
				<VictoryAxis
					label="Skills"
					axisLabelComponent={
						<VictoryLabel
							dy={20}
							style={{
								fontSize: config.axisLabelFontSize * fontScale,
								fill: theme.colors.onSurfaceVariant,
							}}
						/>
					}
					style={{
						axis: {
							stroke: theme.colors.outlineVariant,
							strokeWidth: 0.5,
						},
						ticks: { size: 0, stroke: "transparent" },
						tickLabels: {
							fill: theme.colors.onSurfaceVariant,
							fontSize: config.tickLabelFontSize * fontScale,
							lineHeight: config.tickLabelFontSize * fontScale * 1.2,
						},
						grid: {
							stroke: theme.colors.surfaceVariant,
							strokeWidth: 0.5,
							strokeDasharray: "2,2",
						},
					}}
				/>

				<VictoryAxis
					dependentAxis
					label="Study time per skill (min)"
					axisLabelComponent={
						<VictoryLabel
							dy={-32}
							y={
								config.padding.top +
								(chartHeight - config.padding.top - config.padding.bottom) / 2 +
								8
							}
							style={{
								fontSize: config.axisLabelFontSize * fontScale,
								fill: theme.colors.onSurfaceVariant,
							}}
						/>
					}
					style={{
						axis: {
							stroke: theme.colors.outlineVariant,
							strokeWidth: 0.5,
						},
						ticks: { size: 0, stroke: "transparent" },
						tickLabels: {
							fill: theme.colors.onSurfaceVariant,
							fontSize: config.tickLabelFontSize * fontScale,
						},
						grid: { stroke: "none" },
					}}
					tickFormat={(t) => `${t}m`}
				/>

				<VictoryBar
					data={chartData}
					style={{
						data: {
							fill: (args: { datum?: { fill?: string } }) =>
								args.datum?.fill ?? theme.colors.primary,
						},
					}}
					barWidth={scaledBarWidth}
					categories={{ x: data.map((d) => formatXAxisLabel(d.category)) }}
				/>

				{threshold !== undefined && (
					<VictoryLine
						data={[
							{ x: 0, y: threshold },
							{ x: data.length + 1, y: threshold },
						]}
						style={{
							data: {
								stroke: theme.colors.onSurfaceVariant,
								strokeWidth: config.thresholdLineStrokeWidth,
								strokeDasharray: config.thresholdLineDash.join(","),
							},
						}}
					/>
				)}
			</VictoryChart>

			{threshold !== undefined && (
				<RNText
					style={{
						position: "absolute",
						right: 8,
						top: targetLabelY - targetFontSize - 12,
						fontSize: targetFontSize * 0.92,
						lineHeight: targetFontSize * 0.92,
						color: theme.colors.onSurfaceVariant,
						backgroundColor: theme.colors.surface,
						paddingHorizontal: 6,
						paddingVertical: 2,
						borderRadius: 8,
						borderWidth: 1,
						borderColor: theme.colors.outlineVariant,
					}}
				>
					Recommended min
				</RNText>
			)}
		</View>
	);
};
